import { Button, Chip, Fade, makeStyles, TextField, Tooltip, Typography } from '@material-ui/core';
import { useContext, useEffect, useState } from 'react';
import { Bold } from '../../../components/UI/TextFormatters';
import { transitionTimeout } from './ClaimWizard';
import { separations } from '../../../constants/StyleConstants';
import { AttachFile } from '@material-ui/icons';
import DropZone from '../../../components/UI/DropZone';
import OutlinedDiv from '../../../components/UI/OutlinedDiv';
import { ClaimContext } from '../../../contexts/ClaimContext';
import { AppContext } from '../../../contexts/AppContext';
import ClaimsAPI from '../../../services/ClaimsAPI';
import { uuidv4 } from '../../../utils/MiscellaneousUtils';
import clsx from 'clsx';
import LinearCompletion from '../../../components/UI/LinearCompletion';
import { parseCLDR26 } from '../../../utils/GridUtils';

const useStyles = makeStyles(theme => ({
  ...separations,
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  supplierChip: {
    margin: 2,
  },
  supplierChipsWrapper: {
    marginTop: 10,
  },
  attachmentInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexDirection: 'column',
    rowGap: '1em',
  },
  attachment: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    columnGap: '.5em',
    marginBottom: '.3em',
  },
  attachmentMessage: {
    fontSize: '.8em',
    color: 'rgba(0,0,0,.8)',
  },
}));

const MAX_FILE_SIZE_MB = 7;

const MissingBills = () => {
  const classes = useStyles();
  const { showError, showWarning } = useContext(AppContext);
  const [transIn, setTransIn] = useState(true);
  const [description, setDescription] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [dropZoneTransIn, setDropZoneTransIn] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [remainingFileSpace, setRemainingFileSpace] = useState(MAX_FILE_SIZE_MB);
  const { setStepElements, setOpenBackDrop, selectedClaimId } = useContext(ClaimContext);

  useEffect(() => {
    const step = {
      stepNumber: 1,
      backButtonText: 'Cancelar y elegir otro tipo de reclamo',
      finishButtonText: 'Iniciar reclamo',
      finishButtonAction: () => sendClaim(description, attachedFiles),
      transition: {
        enter: () => setTransIn(true),
        exit: () => setTransIn(false),
      },
    };

    setStepElements(step.stepNumber, step);
  }, [description, attachedFiles, orderNumber]);

  const handleFiles = files => {
    setRemainingFileSpace(remainingFileSpace - files[0].size / 1024 / 1024);
    setAttachedFiles([...attachedFiles, files[0]]);
    setDropZoneTransIn(false);
  };

  const deleteAttachment = attachment => {
    setRemainingFileSpace(remainingFileSpace + attachment.size / 1024 / 1024);
    setAttachedFiles(attachedFiles.filter(file => file !== attachment));
  };

  const validate = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = description.isBlank())) errorMessages.push('Debe ingresar una descripción');

    const hasErrors = errors.some(e => e);

    hasErrors && showWarning(errorMessages);

    return !hasErrors;
  };

  const sendClaim = () => {
    return new Promise((resolve, reject) => {
      if (validate()) {
        setOpenBackDrop(true);

        const request = {
          idType: selectedClaimId,
          description: description,
          files: attachedFiles.length,
          filesId: uuidv4(),
          additionalInfo: {
            orderNumber: !orderNumber.isBlank() ? orderNumber : undefined,
          },
        };

        if (!attachedFiles.isEmpty()) {
          ClaimsAPI.uploadFile(request.filesId, attachedFiles)
            .then(res => {
              ClaimsAPI.startClaim(request)
                .then(res => resolve())
                .catch(error => {
                  setOpenBackDrop(false);
                  showError('Error al iniciar reclamo');
                });
            })
            .catch(error => {
              setOpenBackDrop(false);
              showError('Error al iniciar reclamo, no se pudieron adjuntar los archivos al reclamo');
            });
        } else {
          ClaimsAPI.startClaim(request)
            .then(res => resolve())
            .catch(error => {
              setOpenBackDrop(false);
              showError('Error al iniciar reclamo');
            });
        }
      } else {
        reject();
      }
    });
  };

  return (
    <DropZone
      open={dropZoneTransIn}
      setOpen={setDropZoneTransIn}
      handleFiles={handleFiles}
      acceptedFiles={['application/pdf']}
      maxFileSize={remainingFileSpace}>
      <Fade in={transIn} timeout={transitionTimeout}>
        <div className={classes.root}>
          <Typography variant="subtitle2">
            El envío de documentos fiscales debe realizarse enviándolos vía mail a <Bold>fe.proveedores@acerbrag.com</Bold>, en formato PDF
            (un pdf por cada factura).<br></br>
            <br></br>
            Si ya la envió y no ve reflejado su comprobante en el portal, puede enviarlo adjuntándola aquí, indicando en la descripción
            número de pedido, hojas de servicio y/o remitos.
          </Typography>
          <TextField
            className={classes.topSeparation}
            margin="dense"
            id="description"
            multiline
            rows={5}
            variant="outlined"
            label="Descripción"
            onChange={({ target: { value } }) => setDescription(value.isBlank() ? '' : value)}
            value={description}
          />
          <div>
            {!attachedFiles.isEmpty() && (
              <Fade in={true}>
                <div className={classes.supplierChipsWrapper}>
                  <OutlinedDiv label="Archivos adjuntos">
                    {attachedFiles.map((att, i) => (
                      <Chip
                        key={i}
                        className={classes.supplierChip}
                        size="small"
                        label={att.name}
                        onDelete={() => deleteAttachment(att)}
                        color="primary"
                      />
                    ))}
                  </OutlinedDiv>
                  <LinearCompletion
                    value={100 - (remainingFileSpace * 100) / MAX_FILE_SIZE_MB}
                    label={
                      (remainingFileSpace < 0.1 ? `${Math.trunc(remainingFileSpace * 1024)} KB` : `${parseCLDR26(remainingFileSpace)} MB`) +
                      `/ ${MAX_FILE_SIZE_MB} MB`
                    }
                  />
                </div>
              </Fade>
            )}
          </div>
          <div className={classes.attachmentInfo}>
            <TextField
              style={{ width: '50%' }}
              margin="dense"
              id="orderNumber"
              label="Número/s de pedido/s"
              onChange={({ target: { value } }) => setOrderNumber(value.isBlank() ? '' : value)}
              value={orderNumber}
            />
            <div className={classes.attachment}>
              <Tooltip title={'Adjuntar archivo'} enterDelay={1000}>
                <Button variant="contained" color="primary" startIcon={<AttachFile />} onClick={() => setDropZoneTransIn(true)}>
                  Adjuntar archivo
                </Button>
              </Tooltip>
              <div className={classes.attachmentMessage}>*Puede adjuntar hasta 7 MB en uno o más archivos</div>
            </div>
          </div>
        </div>
      </Fade>
    </DropZone>
  );
};

export default MissingBills;
